import { useCallback, useEffect, useRef, useState } from "react";
import { useDigestConfig, useRoutineMutations } from "../../hooks/useRoutines.ts";
import { cardListClass, cardSurfaceClass } from "@/components/threads/shared.tsx";
import { cn } from "../../lib/utils.ts";
import { Toggle } from "../ui/toggle.tsx";
import { errorToast } from "../ui/toast.tsx";
import { ColorDot } from "./LabelCatalogEditor.tsx";

const sameSet = (a: Set<string>, b: Set<string>) =>
  a.size === b.size && [...a].every((id) => b.has(id));

/**
 * Auto Digest's label picker. The catalog is owned by Auto Inbox, so this lists
 * every classification label (built-in + custom) and lets the user choose which
 * ones the daily digest summarizes. The selection (label ids) is stored on the
 * auto-digest routine's own config.
 *
 * Saves are coalesced: each POST replaces the whole selection, so firing them
 * concurrently on rapid toggles could let an older request land last and clobber
 * the newest choice. Instead we keep at most one save in flight and, when it
 * finishes, save again if the desired selection has moved on — so the server
 * always converges to the latest toggles.
 */
export function DigestLabelSelector({ systemId }: { systemId: string }) {
  const { config, isLoading } = useDigestConfig(systemId);
  const { saveDigestConfig } = useRoutineMutations();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Synchronous mirror of the desired selection so a toggle (and the save loop)
  // can read the latest set without waiting for a re-render.
  const selectedRef = useRef(selected);
  // The selection the server last confirmed — the loop's target and the value we
  // roll back to on a failed save.
  const lastSavedRef = useRef<Set<string>>(new Set());
  // True while the save loop is draining pending toggles (at most one at a time).
  const savingRef = useRef(false);

  // Seed from the server, but never mid-save: a refetch landing during the loop
  // must not clobber the in-flight optimistic selection.
  useEffect(() => {
    if (!config || savingRef.current) return;
    const next = new Set(config.selectedLabelIds);
    selectedRef.current = next;
    lastSavedRef.current = next;
    setSelected(next);
  }, [config]);

  const drainSaves = useCallback(async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    try {
      // Keep saving until the server (lastSaved) matches the latest desired set;
      // intermediate toggles made mid-save are coalesced into the next iteration.
      while (!sameSet(selectedRef.current, lastSavedRef.current)) {
        const attempt = new Set(selectedRef.current);
        try {
          await saveDigestConfig(systemId, { selectedLabelIds: [...attempt] });
          lastSavedRef.current = attempt;
        } catch (err) {
          // Roll the UI back to the last confirmed selection and stop; surface
          // the failure instead of diverging silently or spinning the loop.
          selectedRef.current = lastSavedRef.current;
          setSelected(lastSavedRef.current);
          console.error("Failed to save digest labels", err);
          errorToast("Failed to save digest labels");
          break;
        }
      }
    } finally {
      savingRef.current = false;
    }
  }, [systemId, saveDigestConfig]);

  const toggle = (id: string, on: boolean) => {
    const next = new Set(selectedRef.current);
    if (on) next.add(id);
    else next.delete(id);
    selectedRef.current = next;
    setSelected(next);
    void drainSaves();
  };

  if (isLoading || !config) {
    return <div className="text-sm text-muted-foreground">Loading labels…</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className={cn(cardSurfaceClass, "text-sm text-muted-foreground")}>
        Choose which labels to summarize in your daily digest. Emails with these labels are batched
        into a once-a-day summary instead of being surfaced individually. Manage the labels
        themselves in Auto Inbox.
      </div>
      {config.labels.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          No labels yet. Add labels in Auto Inbox to choose what gets digested.
        </div>
      ) : (
        <div className={cardListClass}>
          {config.labels.map((label) => (
            <div
              key={label.id}
              className={cn(
                cardSurfaceClass,
                "flex items-center justify-between gap-3",
                !label.enabled && "opacity-50",
              )}
            >
              <div className="flex min-w-0 items-center gap-2">
                <ColorDot color={label.color} />
                <span className="truncate text-sm font-medium">{label.name}</span>
              </div>
              <Toggle
                checked={selected.has(label.id)}
                onCheckedChange={(on) => toggle(label.id, on)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
